import { useEffect, useMemo, useState, type FormEvent } from "react";
import * as endpoints from "@/api/endpoints";
import { apiErrorCode, apiErrorMessage } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import type { Plan } from "@/api/types";
import { formatYen } from "@/utils/format";

interface PlanInfo {
  key: Plan;
  label: string;
  tagline: string;
  tiers: string;
  rank: number;
}

const PLANS: PlanInfo[] = [
  {
    key: "light",
    label: "Light",
    tagline: "入門プラン",
    tiers: "基本商品のみ購入可",
    rank: 1,
  },
  {
    key: "standard",
    label: "Standard",
    tagline: "主力プラン",
    tiers: "多くの商品が購入可",
    rank: 2,
  },
  {
    key: "premium",
    label: "Premium",
    tagline: "最上位プラン",
    tiers: "全商品が購入可、Shopee 連携機能を利用可",
    rank: 3,
  },
];

interface Props {
  /** ダイアログを開いた時点で初期選択するプラン（🔒商品からの導線で指定） */
  initialPlan?: Plan;
  /** 閉じる時の callback。変更なしでも呼ばれる。 */
  onClose: () => void;
  /** 変更成功時の callback（AuthContext のリフレッシュ後に呼ばれる）。 */
  onChanged?: (result: endpoints.PlanChangeResult) => void;
}

/**
 * プラン変更ダイアログ。アカウント画面・カタログから再利用できるよう共通化。
 */
export function PlanChangeDialog({ initialPlan, onClose, onChanged }: Props) {
  const { user, refreshMe } = useAuth();
  const currentPlan = (user?.tenant?.plan ?? "light") as Plan;
  const currentRank = PLANS.find((p) => p.key === currentPlan)?.rank ?? 1;

  const [selected, setSelected] = useState<Plan>(initialPlan ?? currentPlan);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<endpoints.PlanChangePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const selectedInfo = useMemo(
    () => PLANS.find((p) => p.key === selected) ?? PLANS[0],
    [selected],
  );
  const changeType: "upgrade" | "downgrade" | null =
    selected === currentPlan
      ? null
      : selectedInfo.rank > currentRank
      ? "upgrade"
      : "downgrade";

  // 選択変更のたびにプレビュー取得（アップグレードのときのみ意味がある）
  useEffect(() => {
    let cancelled = false;
    if (selected === currentPlan) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    setPreview(null);
    setPreviewError(null);
    setPreviewLoading(true);
    (async () => {
      try {
        const res = await endpoints.previewPlanChange(selected);
        if (!cancelled) setPreview(res);
      } catch (e) {
        if (!cancelled)
          setPreviewError(apiErrorMessage(e, "金額プレビューの取得に失敗しました"));
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, currentPlan]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (selected === currentPlan) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await endpoints.changePlan(selected);
      // AuthContext の user を最新化（カタログのロック解除等のため）
      await refreshMe();
      onChanged?.(result);
      onClose();
    } catch (err) {
      const code = apiErrorCode(err);
      if (code === "PAYMENT_NOT_ACTIVE") {
        setError(
          "サブスクリプションが正常でないため変更できません。お支払い情報をご確認ください。",
        );
      } else if (code === "CANCELLATION_IN_PROGRESS") {
        setError(
          "退会申請中のため変更できません。アカウント画面で退会申請を取り消してください。",
        );
      } else if (code === "SAME_PLAN") {
        setError("既に同じプランです。");
      } else {
        setError(apiErrorMessage(err, "プラン変更に失敗しました"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-full overflow-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">プラン変更</h3>
          <button
            type="button"
            className="text-gray-500 hover:text-gray-700"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        <form onSubmit={onSubmit} className="p-4 space-y-3">
          <div className="text-sm text-gray-600">
            現在のプラン:{" "}
            <span className="font-semibold uppercase">{currentPlan}</span>
          </div>

          <div className="space-y-2">
            {PLANS.map((p) => {
              const isCurrent = p.key === currentPlan;
              const isSelected = p.key === selected;
              return (
                <label
                  key={p.key}
                  className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer ${
                    isSelected
                      ? "border-brand-500 bg-brand-50"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="plan"
                    value={p.key}
                    checked={isSelected}
                    onChange={() => setSelected(p.key)}
                    disabled={submitting}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium flex items-center gap-2">
                      <span className="uppercase">{p.label}</span>
                      {isCurrent && (
                        <span className="badge-gray text-[10px]">現在</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">{p.tagline}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {p.tiers}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          {changeType === "upgrade" && (
            <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-800 space-y-1">
              <div>
                ⬆️ <strong>アップグレード</strong>: 変更は即時反映され、これまでロック表示だった商品がご購入可能になります。
              </div>
              {previewLoading ? (
                <div className="text-gray-500">金額を計算中...</div>
              ) : previewError ? (
                <div className="text-red-700">{previewError}</div>
              ) : preview ? (
                preview.is_trial ? (
                  <div>
                    現在トライアル期間中のため、今回の変更で追加の課金は発生しません。
                    トライアル終了後から新プランの料金（月額）が適用されます。
                  </div>
                ) : preview.amount_due !== null && preview.amount_due > 0 ? (
                  <div>
                    <strong className="text-green-900">
                      今すぐ {formatYen(preview.amount_due)} が請求されます
                    </strong>
                    （日割り計算）。お支払い方法が未登録の場合は変更できません。
                  </div>
                ) : (
                  <div>追加の課金は発生しません。</div>
                )
              ) : null}
            </div>
          )}
          {changeType === "downgrade" && (
            <div className="rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 text-xs text-yellow-800">
              ⬇️ <strong>ダウングレード</strong>: 変更は<u>次回請求サイクルから</u>適用されます。それまでは現在のプランのままご利用いただけます。カートに該当商品がある場合は発注前にご確認ください。
            </div>
          )}

          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting || selected === currentPlan}
            >
              {submitting
                ? "変更中..."
                : selected === currentPlan
                ? "プランを選択してください"
                : `${selectedInfo.label} に変更`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
